import React, { useMemo } from 'react';
import { StaticScreenMap, brandDot } from './StaticScreenMap';

// The one map used everywhere: free Esri Light Gray tiles recolored mint green (no
// baked-in labels), deep-green round markers, framed on the core NYC area (Lower Manhattan to the Upper West
// Side, Brooklyn waterfront to the Hudson). Screens outside that area are
// drawn as faint edge markers with arrows instead of stretching the frame.
// Static (no hover/zoom) so it also rasterizes cleanly into the PDF exports.
const CORE = { minLon: -74.02, maxLon: -73.96, minLat: 40.70, maxLat: 40.79 };

// A short, deliberate set of names: boroughs/state, the rivers, and a single
// street label (no neighborhoods).
const LABELS = [
  { text: 'New Jersey', lat: 40.742, lon: -74.036, kind: 'place' as const },
  { text: 'Hudson River', lat: 40.738, lon: -74.018, rotate: -61, kind: 'water' as const },
  { text: 'Manhattan', lat: 40.7385, lon: -73.978, kind: 'place' as const },
  { text: 'East River', lat: 40.723, lon: -73.970, rotate: -50, kind: 'water' as const },
  { text: 'Brooklyn', lat: 40.703, lon: -73.953, kind: 'place' as const },
  { text: 'Queens', lat: 40.752, lon: -73.935, kind: 'place' as const },
  { text: 'FDR Dr', lat: 40.7385, lon: -73.9735, rotate: -61, kind: 'street' as const },
];

interface Props {
  screens: { lat: number; lon: number }[];
  // Request frame in px. Default is square; markers and framing are computed
  // against this shape, so the rendered box should match its aspect ratio.
  width?: number;
  height?: number;
  dot?: number;
  pad?: number;
  // Stretch to the parent's height (used for the report tiles) instead of
  // holding the frame's aspect ratio.
  fill?: boolean;
  minHeight?: number;
  // Cap the square map's width on wide cards.
  maxWidth?: number;
  showNote?: boolean;
  // Zoom +/- and drag to pan. Defaults on for the square maps, off for the
  // small report tiles (`fill`).
  controls?: boolean;
  style?: React.CSSProperties;
  noteStyle?: React.CSSProperties;
  onFail?: () => void;
}

export function StaticNetworkMap({
  screens, width = 640, height = 640, dot = 14, pad = 40, fill, minHeight, maxWidth, showNote = true, controls, style, noteStyle, onFail,
}: Props) {
  const pins = useMemo(() => screens.filter(s => s.lat && s.lon), [screens]);
  const core = useMemo(
    () => pins.filter(p => p.lon > CORE.minLon && p.lon < CORE.maxLon && p.lat > CORE.minLat && p.lat < CORE.maxLat),
    [pins]
  );
  // Fall back to every pin if none land in the core box (e.g. non-NYC data).
  const framed = core.length ? core : pins;
  const outliers = useMemo(() => (core.length ? pins.filter(p => !core.includes(p)) : []), [pins, core]);
  const outside = outliers.length;

  if (!pins.length) return null;

  return (
    <div style={{ width: '100%', maxWidth, margin: maxWidth ? '0 auto' : undefined, height: fill ? '100%' : undefined }}>
      <StaticScreenMap
        screens={framed}
        outliers={outliers}
        width={width}
        height={height}
        dot={dot}
        pad={pad}
        maxZoom={15}
        zoomControls={controls ?? !fill}
        tiles="esri-light"
        labels={LABELS}
        renderDot={() => ({ ...brandDot(), d: dot })}
        onFail={onFail}
        style={{
          width: '100%', borderRadius: 12, border: '1px solid var(--border-soft)',
          ...(fill ? { height: '100%', minHeight } : { aspectRatio: `${width} / ${height}` }),
          ...style,
        }}
      />
      {showNote && outside > 0 && (
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 8, ...noteStyle }}>
          Arrows point to {outside} more screen{outside === 1 ? '' : 's'} beyond this view
        </div>
      )}
    </div>
  );
}
